import { apiAgent } from "@/network/axiosAgent";
import type {
  AcceptSurfaceAssetBody,
  FetchSurfaceFeedParams,
  PromoteSurfaceAsset,
  SurfaceBulkBody,
  SurfaceBulkResponse,
  SurfaceFeedResponse,
  SurfaceFeedVerificationStatus,
  SurfaceProgress,
  SurfaceSolutionTag,
} from "./types";

const surfaceBase = (slug: string) => `promote/board/${slug}/surface`;

function normalizeFeedPayload(data: unknown): SurfaceFeedResponse {
  const raw = (data ?? {}) as Record<string, unknown>;
  const assets = (raw.assets ?? raw.results ?? raw.items ?? []) as PromoteSurfaceAsset[];
  return {
    assets: Array.isArray(assets) ? assets : [],
    next_cursor: (raw.next_cursor as number | string | null | undefined) ?? null,
    progress: raw.progress as SurfaceProgress | undefined,
  };
}

function asStatusList(
  value: SurfaceFeedVerificationStatus | SurfaceFeedVerificationStatus[]
): SurfaceFeedVerificationStatus[] {
  return Array.isArray(value) ? value : [value];
}

/** Builds feed query string with repeatable `verification_status` params. */
export function buildSurfaceFeedSearch(params: FetchSurfaceFeedParams): string {
  const search = new URLSearchParams();
  for (const status of asStatusList(params.verificationStatus)) {
    search.append("verification_status", status);
  }
  search.set("limit", String(params.limit ?? 50));
  if (params.cursor != null && params.cursor !== "") {
    search.set("cursor", String(params.cursor));
  }
  return search.toString();
}

export function fetchSurfaceProgress(slug: string) {
  return apiAgent.get<SurfaceProgress>(`${surfaceBase(slug)}/progress`).then((res) => res.data);
}

export function fetchSurfaceFeed(slug: string, params: FetchSurfaceFeedParams) {
  return apiAgent
    .get(`${surfaceBase(slug)}/feed?${buildSurfaceFeedSearch(params)}`)
    .then((res) => normalizeFeedPayload(res.data));
}

export function fetchSurfaceSolutionTags(slug: string) {
  return apiAgent
    .get<SurfaceSolutionTag[] | { tags?: SurfaceSolutionTag[]; results?: SurfaceSolutionTag[] }>(
      `${surfaceBase(slug)}/solution-tags`
    )
    .then((res) => {
      const data = res.data;
      if (Array.isArray(data)) return data;
      return data?.tags ?? data?.results ?? [];
    });
}

export function acceptSurfaceAsset(slug: string, assetId: number, body: AcceptSurfaceAssetBody) {
  return apiAgent
    .post<PromoteSurfaceAsset>(`${surfaceBase(slug)}/assets/${assetId}/accept`, body)
    .then((res) => res.data);
}

export function rejectSurfaceAsset(slug: string, assetId: number) {
  return apiAgent
    .post<PromoteSurfaceAsset>(`${surfaceBase(slug)}/assets/${assetId}/reject`, {})
    .then((res) => res.data);
}

export function skipSurfaceAsset(slug: string, assetId: number) {
  return apiAgent
    .post<PromoteSurfaceAsset>(`${surfaceBase(slug)}/assets/${assetId}/skip`, {})
    .then((res) => res.data);
}

export function reprocessSurfaceAsset(slug: string, assetId: number) {
  return apiAgent
    .post<PromoteSurfaceAsset | { detail?: string }>(
      `${surfaceBase(slug)}/assets/${assetId}/reprocess`,
      {}
    )
    .then((res) => res.data);
}

export function bulkSurfaceAction(slug: string, body: SurfaceBulkBody) {
  return apiAgent
    .post<SurfaceBulkResponse>(`${surfaceBase(slug)}/bulk`, body)
    .then((res) => res.data);
}
